// 组装登录后的桌面壳：
//   注册表加载 → 安装 SDK 运行时 → 渲染 Desktop + Taskbar + WindowHost。
// 注册表在挂载时加载；此外页面重新可见 / 获得焦点时也会重拉（registry + 桌面布局），
// 这样在「能力中心」等独立标签页里安装/卸载 solution 后，切回桌面即可看到图标增删，
// 不必手动刷新页面。
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ResolvedRegistry } from '@blade/os-shell';
import { loadRegistry } from '../apps/loadRegistry';
import { RegistryContext, type Registry } from '../apps/registryContext';
import { useDesktopLayoutStore } from '../store/desktopLayoutStore';
import { Desktop } from '../shell/Desktop';
import { Taskbar } from '../shell/Taskbar';
import { NotificationCenter } from '../shell/NotificationCenter';
import { WindowHost } from '../window-manager/WindowHost';
import { makeLaunchApp, type ResolvedRegistryEntry } from '../runtime/launchApp';
import { installSdkRuntime } from '../runtime/installSdkRuntime';

function toContextRegistry(r: ResolvedRegistry): Registry {
  const byId = new Map(r.entries.map((e) => [e.id, e]));
  return {
    entries: r.entries,
    get: (id) => byId.get(id),
  };
}

export function ProtectedShell() {
  const [registry, setRegistry] = useState<Registry | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Use ref so refreshRegistry always sees latest registry setter
  const registryRef = useRef(registry);
  registryRef.current = registry;

  const doLoadRegistry = useCallback(async () => {
    const r = await loadRegistry();
    setRegistry(toContextRegistry(r));
  }, []);

  useEffect(() => {
    let cancelled = false;
    doLoadRegistry().catch(() => {
      if (!cancelled) setError('APP 注册表加载失败');
    });
    return () => {
      cancelled = true;
    };
  }, [doLoadRegistry]);

  // 每个调用方(应用市场安装/卸载、服务管理手动上线等)调这个都是因为桌面
  // 图标该增删了——只刷 registry 不够,图标要不要渲染由 registry 决定,但
  // 图标出不出现在桌面上是布局 load() 的服务端 reconcile 决定的(见下面
  // visibilitychange 那段注释),两个不一起刷会出现"装完看不到,刷新网页才
  // 出现"这种滞后。
  const refreshRegistry = useCallback(async () => {
    await Promise.all([doLoadRegistry(), useDesktopLayoutStore.getState().load()]);
  }, [doLoadRegistry]);

  // 页面重新可见 / 窗口获得焦点时重拉 registry + 桌面布局：覆盖「在别的标签页
  // （能力中心等）安装/卸载了 app」这种本页收不到通知的场景。registry 决定图标
  // 是否渲染（Desktop 对 registry 里没有的 appId 直接跳过 → 卸载即消失），布局
  // load() 走服务端 reconcile 追加新装的 app（→ 安装即出现）。
  // 去抖：visibilitychange 与 focus 可能同时触发，1s 内只刷一次。
  useEffect(() => {
    let last = 0;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - last < 1000) return;
      last = now;
      doLoadRegistry().catch(() => {});
      useDesktopLayoutStore.getState().load().catch(() => {});
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [doLoadRegistry]);

  const launch = useMemo(() => {
    if (!registry) return null;
    const fn = makeLaunchApp({
      registry: (id) => {
        const e = registry.get(id);
        if (!e) return undefined;
        return {
          id: e.id,
          title: e.title,
          icon: e.icon,
          singleton: e.singleton,
          defaultSize: e.defaultSize,
          load: e.load,
          externalUrl: e.externalUrl,
          openIn: e.openIn,
        } satisfies ResolvedRegistryEntry;
      },
    });
    installSdkRuntime({ launchApp: fn, registry, refreshRegistry });
    return fn;
  }, [registry, refreshRegistry]);

  // Handle #open-app?id=<appId>[&...params] —— 外部(如 skill_registry「上传镜像」)
  // 深链直接打开指定 app。未知 id / 无权限时 launch 自行忽略。
  useEffect(() => {
    if (!launch) return;
    const hash = window.location.hash;
    if (!hash.startsWith('#open-app')) return;
    const params = new URLSearchParams(hash.replace(/^#open-app\??/, ''));
    const id = params.get('id');
    if (!id) return;
    const rest: Record<string, string> = {};
    params.forEach((v, k) => {
      if (k !== 'id') rest[k] = v;
    });
    history.replaceState(null, '', window.location.pathname + window.location.search);
    launch(id, Object.keys(rest).length ? { params: rest } : undefined);
  }, [launch]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface text-foreground">
        <div className="max-w-md rounded-lg border border-danger/30 bg-surface-muted p-6 text-center">
          <h1 className="text-lg font-semibold">桌面初始化失败</h1>
          <p role="alert" className="mt-2 text-sm text-foreground-muted">{error}</p>
        </div>
      </div>
    );
  }
  if (!registry || !launch) {
    return (
      <div className="min-h-screen flex items-center justify-center text-foreground-muted">
        加载桌面…
      </div>
    );
  }

  return (
    <RegistryContext.Provider value={registry}>
      <div className="os-shell">
        <Desktop onLaunch={(id) => launch(id)} />
        <WindowHost />
        <Taskbar onLaunch={(id) => launch(id)} />
        <NotificationCenter />
      </div>
    </RegistryContext.Provider>
  );
}
